import { useState } from "react";
import { uid } from "../utils";
import { Tag, Btn, Modal, Field, Inp, Sel, Txta, FormRow, BlockHead, Chips, ExpandTable, Explica, KpiStrip } from "./primitives";

const NV_TONE = { "CRÍTICO": "bad", "ALTO": "bad", "MÉDIO": "warn", "INFO": "" };
const PV_TONE = { "MÁXIMA": "bad", "ALTA": "warn", "MÉDIA": "warn", "BAIXA": "" };

const PV = {
  "MÁXIMA": { bg: "var(--danger-soft)",     color: "var(--danger-ink)" },
  "ALTA":   { bg: "var(--cat-orange-soft)", color: "var(--cat-orange-ink)" },
  "MÉDIA":  { bg: "var(--warn-soft)",       color: "var(--warn-ink)" },
  "BAIXA":  { bg: "var(--bg)", color: "var(--muted)" },
};
const FV = {
  TOF: { bg: "var(--cat-violet-soft)", color: "var(--cat-violet-ink)" },
  RTG: { bg: "var(--success-soft)",    color: "var(--success-ink)" },
  BOF: { bg: "var(--cat-purple-soft)", color: "var(--cat-purple-ink)" },
};

// ── Renderers ─────────────────────────────────────────────────────────────────

function RenderDiagnostico({ dados }) {
  if (!dados) return null;
  const d = dados;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {(d.conta || d.data_auditoria) && (
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          {d.conta && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 2 }}>Conta</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{d.conta}</div>
              {d.conta_id && <div style={{ fontSize: 11, color: "var(--muted)" }}>{d.conta_id}</div>}
            </div>
          )}
          {d.data_auditoria && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 2 }}>Auditoria</div>
              <div style={{ fontSize: 13, color: "var(--ink)" }}>{d.data_auditoria}</div>
            </div>
          )}
        </div>
      )}

      {d.resumo && (
        <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>{d.resumo}</div>
      )}

      {d.metricas?.length > 0 && (
        <KpiStrip items={d.metricas.map((m) => ({
          label: m.l, value: m.v, sub: m.sub,
          tone: m.alert ? "bad" : m.hi ? "ok" : "",
          color: m.alert ? "var(--danger)" : m.hi ? "var(--success)" : undefined,
        }))} />
      )}

      {d.funil?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Funil</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {d.funil.map((f, i) => (
              <div key={f.label} style={{ display: "flex", alignItems: "center", flex: 1 }}>
                <div style={{
                  flex: 1, padding: "10px 12px", borderRadius: 8, textAlign: "center",
                  background: f.ok ? "var(--success-soft)" : "var(--danger-soft)",
                  border: `1px solid ${f.ok ? "var(--success)" : "var(--danger)"}`,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: f.ok ? "var(--success-ink)" : "var(--danger-ink)" }}>{f.label}</div>
                  {f.desc && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{f.desc}</div>}
                  <div style={{ fontSize: 10, fontWeight: 600, color: f.ok ? "var(--success-ink)" : "var(--danger-ink)", marginTop: 3 }}>
                    {f.ok ? "✓ Ativo" : "✗ Inexistente"}
                  </div>
                </div>
                {i < d.funil.length - 1 && <span style={{ color: "var(--faint)", padding: "0 6px" }}>→</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
        {d.gargalos?.length > 0 && (
          <div style={{ display: "grid", gap: 8, alignContent: "start" }}>
            <span className="eyebrow">Gargalos ({d.gargalos.length})</span>
            {d.gargalos.map((g, i) => (
              <Explica key={i} tone={NV_TONE[g.nivel]} title={<>{g.titulo} <span className="sub" style={{ fontFamily: "var(--font-data)", fontSize: 11 }}>· {g.nivel}</span></>}>
                {g.desc}
              </Explica>
            ))}
          </div>
        )}
        {d.oportunidades?.length > 0 && (
          <div style={{ display: "grid", gap: 8, alignContent: "start" }}>
            <span className="eyebrow">Oportunidades ({d.oportunidades.length})</span>
            {d.oportunidades.map((o, i) => (
              <Explica key={i} tone={PV_TONE[o.prio]} title={<>{o.titulo} <span className="sub" style={{ fontFamily: "var(--font-data)", fontSize: 11 }}>· {o.prio}</span></>}>
                {o.desc}
              </Explica>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RenderCriativos({ dados }) {
  if (!dados) return null;
  const d = dados;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {(d.icp || d.principio) && (
        <div style={{ padding: "10px 14px", background: "var(--primary-soft)", borderRadius: 8, border: `1px solid ${"var(--primary)"}40` }}>
          {d.icp && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--primary)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3 }}>ICP</div>
              <div style={{ fontSize: 12, color: "var(--ink)", marginBottom: d.principio ? 6 : 0 }}>{d.icp}</div>
            </>
          )}
          {d.principio && (
            <div style={{ fontSize: 12, color: "var(--muted)" }}>
              <strong style={{ color: "var(--ink)" }}>Princípio:</strong> {d.principio}
            </div>
          )}
        </div>
      )}

      {d.criativos?.map((cr, i) => {
        const fc = FV[cr.fase] || { bg: "var(--bg)", color: "var(--muted)" };
        const pc = PV[cr.prio] || { bg: "var(--bg)", color: "var(--faint)" };
        return (
          <div key={i} style={{ padding: "12px 16px", background: "var(--bg)", borderRadius: 8, border: `1px solid ${"var(--line-soft)"}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{cr.id}</span>
              {cr.fase && <Tag label={cr.fase} bg={fc.bg} color={fc.color} xs />}
              {cr.prio && <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 99, background: pc.bg, color: pc.color }}>PRIORIDADE {cr.prio}</span>}
              {cr.formato && <span style={{ fontSize: 11, color: "var(--faint)", marginLeft: "auto" }}>{cr.formato}</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {cr.hook && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>Hook</div>
                  <div style={{ fontSize: 12, color: "var(--ink)", fontStyle: "italic", lineHeight: 1.5 }}>{cr.hook}</div>
                </div>
              )}
              <div>
                {cr.objetivo && (
                  <>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>Objetivo</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.5, marginBottom: 8 }}>{cr.objetivo}</div>
                  </>
                )}
                {cr.cta && (
                  <>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>CTA</div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>{cr.cta}</div>
                  </>
                )}
              </div>
            </div>
            {cr.campanha && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${"var(--line-soft)"}`, fontSize: 11, color: "var(--faint)" }}>Campanha: {cr.campanha}</div>
            )}
          </div>
        );
      })}

      {d.ctas?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>CTAs por Fase</div>
          <div style={{ display: "flex", gap: 8 }}>
            {d.ctas.map((f, i) => {
              const fc = FV[f.fase] || { bg: "var(--bg)", color: "var(--muted)" };
              return (
                <div key={i} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, background: fc.bg, border: `1px solid ${fc.color}30` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: fc.color, marginBottom: 3 }}>{f.fase}</div>
                  <div style={{ fontSize: 12, color: "var(--ink)", fontWeight: 600, marginBottom: 2 }}>{f.cta}</div>
                  {f.tom && <div style={{ fontSize: 11, color: "var(--muted)" }}>{f.tom}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function RenderPlano({ entry, onUpdate }) {
  const [updating, setUpdating] = useState(false);
  const d = entry.dados;

  async function toggleCheck(gi, ii) {
    if (updating) return;
    setUpdating(true);
    const nd = JSON.parse(JSON.stringify(d));
    nd.checklists[gi].items[ii].done = !nd.checklists[gi].items[ii].done;
    await onUpdate(entry.id, { dados: nd });
    setUpdating(false);
  }

  if (!d) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {d.campanhas?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Campanhas</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {d.campanhas.map((camp, i) => {
              const fc = FV[camp.fase] || { bg: "var(--bg)", color: "var(--muted)" };
              return (
                <div key={i} style={{ flex: "1 1 200px", padding: "12px 14px", borderRadius: 8, background: fc.bg, border: `1px solid ${fc.color}30` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)" }}>{camp.nome}</span>
                    {camp.acao && <Tag label={camp.acao} bg={fc.bg} color={fc.color} xs />}
                  </div>
                  {camp.budget && <div style={{ fontSize: 20, fontWeight: 800, color: fc.color, marginBottom: 4 }}>{camp.budget}</div>}
                  {camp.objetivo && <div style={{ fontSize: 12, color: "var(--ink)", fontWeight: 600, marginBottom: 3 }}>{camp.objetivo}</div>}
                  {camp.detalhe && <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.4 }}>{camp.detalhe}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {d.checklists?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Checklists</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {d.checklists.map((grupo, gi) => {
              const done = grupo.items.filter((it) => it.done).length;
              return (
                <div key={gi} style={{ padding: "12px 14px", background: "var(--bg)", borderRadius: 8, border: `1px solid ${"var(--line-soft)"}` }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>{grupo.grupo}</div>
                    <span style={{ fontSize: 10, color: "var(--faint)" }}>{done}/{grupo.items.length}</span>
                  </div>
                  {grupo.items.map((item, ii) => (
                    <div
                      key={ii}
                      onClick={() => toggleCheck(gi, ii)}
                      style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0", cursor: "pointer" }}
                    >
                      <div style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 1,
                        border: `2px solid ${item.done ? "var(--primary)" : "var(--line)"}`,
                        background: item.done ? "var(--primary)" : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {item.done && <span style={{ color: "#fff", fontSize: 9, fontWeight: 700 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 12, color: item.done ? "var(--faint)" : "var(--ink)", textDecoration: item.done ? "line-through" : "none", lineHeight: 1.4 }}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {d.metricas?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Métricas Alvo</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${d.metricas.length}, 1fr)`, gap: 12 }}>
            {d.metricas.map((mf, i) => {
              const fc = FV[mf.fase] || { bg: "var(--bg)", color: "var(--muted)" };
              return (
                <div key={i}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: fc.color, marginBottom: 6 }}>{mf.fase}</div>
                  {(mf.items || []).map((m, j) => (
                    <div key={j} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: `1px solid ${"var(--line-soft)"}` }}>
                      <span style={{ flex: 1, fontSize: 12, color: "var(--muted)" }}>{m.metrica}</span>
                      <span style={{ fontSize: 12, color: "var(--success-ink)", fontWeight: 700 }}>{m.meta}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {d.calendario?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Calendário</div>
          {d.calendario.map((e, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "6px 0", borderBottom: i < d.calendario.length - 1 ? `1px solid ${"var(--line-soft)"}` : "none" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--primary)", minWidth: 72, flexShrink: 0 }}>{e.data}</span>
              <span style={{ fontSize: 12, color: "var(--ink)" }}>{e.acao}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Helpers de formulário ─────────────────────────────────────────────────────

function upArr(setF, key, index, field, value) {
  setF((p) => {
    const arr = [...p[key]];
    arr[index] = { ...arr[index], [field]: value };
    return { ...p, [key]: arr };
  });
}
function addArr(setF, key, item) {
  setF((p) => ({ ...p, [key]: [...p[key], item] }));
}
function rmArr(setF, key, index) {
  setF((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== index) }));
}

// ── DiagnosticoModal ──────────────────────────────────────────────────────────

function DiagnosticoModal({ clientId, onClose, onSave }) {
  const [f, setF] = useState({
    titulo: "", mes: "", conta: "", conta_id: "", data_auditoria: "", resumo: "",
    metricas: [],
    funil: [
      { label: "TOF", desc: "", ok: false },
      { label: "MOF", desc: "", ok: false },
      { label: "BOF", desc: "", ok: false },
    ],
    gargalos: [],
    oportunidades: [],
  });
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!f.titulo.trim()) return;
    setSaving(true);
    await onSave({
      section: "diagnostico", titulo: f.titulo.trim(), mes: f.mes.trim(),
      dados: {
        conta: f.conta, conta_id: f.conta_id, data_auditoria: f.data_auditoria, resumo: f.resumo,
        metricas: f.metricas.filter((m) => m.l && m.v),
        funil: f.funil.filter((ff) => ff.desc || ff.ok),
        gargalos: f.gargalos.filter((g) => g.titulo).map((g, i) => ({ ...g, n: i + 1 })),
        oportunidades: f.oportunidades.filter((o) => o.titulo).map((o, i) => ({ ...o, n: i + 1 })),
      },
    });
    setSaving(false);
  }

  return (
    <Modal title="Nova auditoria Meta Ads" onClose={onClose} width={700}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: "72vh", overflowY: "auto", paddingRight: 4 }}>
        <FormRow>
          <Field label="Título" half><Inp value={f.titulo} onChange={(e) => up("titulo", e.target.value)} placeholder="Auditoria Meta Ads — Maio 2026" /></Field>
          <Field label="Mês" half><Inp value={f.mes} onChange={(e) => up("mes", e.target.value)} placeholder="05/2026" /></Field>
        </FormRow>
        <FormRow>
          <Field label="Conta" half><Inp value={f.conta} onChange={(e) => up("conta", e.target.value)} placeholder="RC2 TRF Interno" /></Field>
          <Field label="ID da conta" half><Inp value={f.conta_id} onChange={(e) => up("conta_id", e.target.value)} placeholder="869033..." /></Field>
        </FormRow>
        <Field label="Data da auditoria"><Inp value={f.data_auditoria} onChange={(e) => up("data_auditoria", e.target.value)} placeholder="13 de maio de 2026" /></Field>
        <Field label="Resumo executivo"><Txta rows={3} value={f.resumo} onChange={(e) => up("resumo", e.target.value)} placeholder="Situação atual da conta..." /></Field>

        {/* Métricas */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>Métricas</div>
            <Btn sm variant="ghost" onClick={() => addArr(setF, "metricas", { l: "", v: "", sub: "", hi: false, alert: false })}>+ Adicionar</Btn>
          </div>
          {f.metricas.map((m, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <Inp style={{ width: 110 }} value={m.l} onChange={(e) => upArr(setF, "metricas", i, "l", e.target.value)} placeholder="Label" />
              <Inp style={{ width: 90 }} value={m.v} onChange={(e) => upArr(setF, "metricas", i, "v", e.target.value)} placeholder="Valor" />
              <Inp style={{ flex: 1 }} value={m.sub} onChange={(e) => upArr(setF, "metricas", i, "sub", e.target.value)} placeholder="Subtexto" />
              <Sel style={{ width: 90 }} value={m.hi ? "bom" : m.alert ? "alerta" : "normal"}
                onChange={(e) => {
                  upArr(setF, "metricas", i, "hi", e.target.value === "bom");
                  upArr(setF, "metricas", i, "alert", e.target.value === "alerta");
                }}>
                <option value="normal">Normal</option>
                <option value="bom">Bom</option>
                <option value="alerta">Alerta</option>
              </Sel>
              <button onClick={() => rmArr(setF, "metricas", i)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>

        {/* Funil */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Funil</div>
          {f.funil.map((ff, i) => (
            <div key={ff.label} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", width: 32, flexShrink: 0 }}>{ff.label}</span>
              <Inp style={{ flex: 1 }} value={ff.desc} onChange={(e) => upArr(setF, "funil", i, "desc", e.target.value)} placeholder={`Campanha / desc ${ff.label}`} />
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted)", cursor: "pointer", flexShrink: 0 }}>
                <input type="checkbox" checked={ff.ok} onChange={(e) => upArr(setF, "funil", i, "ok", e.target.checked)} style={{ accentColor: "var(--success)" }} />
                Ativo
              </label>
            </div>
          ))}
        </div>

        {/* Gargalos */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>Gargalos</div>
            <Btn sm variant="ghost" onClick={() => addArr(setF, "gargalos", { nivel: "ALTO", titulo: "", desc: "" })}>+ Adicionar</Btn>
          </div>
          {f.gargalos.map((g, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
              <Sel style={{ width: 100 }} value={g.nivel} onChange={(e) => upArr(setF, "gargalos", i, "nivel", e.target.value)}>
                {["CRÍTICO", "ALTO", "MÉDIO", "INFO"].map((n) => <option key={n}>{n}</option>)}
              </Sel>
              <Inp style={{ width: 170 }} value={g.titulo} onChange={(e) => upArr(setF, "gargalos", i, "titulo", e.target.value)} placeholder="Título" />
              <Inp style={{ flex: 1, minWidth: 140 }} value={g.desc} onChange={(e) => upArr(setF, "gargalos", i, "desc", e.target.value)} placeholder="Descrição" />
              <button onClick={() => rmArr(setF, "gargalos", i)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 16, lineHeight: 1, marginTop: 6 }}>×</button>
            </div>
          ))}
        </div>

        {/* Oportunidades */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>Oportunidades</div>
            <Btn sm variant="ghost" onClick={() => addArr(setF, "oportunidades", { prio: "ALTA", titulo: "", desc: "" })}>+ Adicionar</Btn>
          </div>
          {f.oportunidades.map((o, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
              <Sel style={{ width: 100 }} value={o.prio} onChange={(e) => upArr(setF, "oportunidades", i, "prio", e.target.value)}>
                {["ALTA", "MÉDIA", "BAIXA"].map((p) => <option key={p}>{p}</option>)}
              </Sel>
              <Inp style={{ width: 170 }} value={o.titulo} onChange={(e) => upArr(setF, "oportunidades", i, "titulo", e.target.value)} placeholder="Título" />
              <Inp style={{ flex: 1, minWidth: 140 }} value={o.desc} onChange={(e) => upArr(setF, "oportunidades", i, "desc", e.target.value)} placeholder="Descrição" />
              <button onClick={() => rmArr(setF, "oportunidades", i)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 16, lineHeight: 1, marginTop: 6 }}>×</button>
            </div>
          ))}
        </div>

        <Btn variant="accent" full onClick={save} disabled={saving || !f.titulo.trim()}>
          {saving ? "Salvando..." : "Salvar auditoria"}
        </Btn>
      </div>
    </Modal>
  );
}

// ── CriativosModal ────────────────────────────────────────────────────────────

function CriativosModal({ clientId, onClose, onSave }) {
  const [f, setF] = useState({
    titulo: "", mes: "", icp: "", principio: "",
    criativos: [],
    ctas: [
      { fase: "TOF", cta: "", tom: "Não comercial" },
      { fase: "RTG", cta: "", tom: "Diagnóstico, não venda" },
      { fase: "BOF", cta: "", tom: "Ação direta" },
    ],
  });
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!f.titulo.trim()) return;
    setSaving(true);
    await onSave({
      section: "criativos", titulo: f.titulo.trim(), mes: f.mes.trim(),
      dados: {
        icp: f.icp, principio: f.principio,
        criativos: f.criativos.filter((c) => c.id),
        ctas: f.ctas.filter((c) => c.cta),
      },
    });
    setSaving(false);
  }

  return (
    <Modal title="Novo guia de criativos" onClose={onClose} width={700}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: "72vh", overflowY: "auto", paddingRight: 4 }}>
        <FormRow>
          <Field label="Título" half><Inp value={f.titulo} onChange={(e) => up("titulo", e.target.value)} placeholder="Guia de Criativos — Maio 2026" /></Field>
          <Field label="Mês" half><Inp value={f.mes} onChange={(e) => up("mes", e.target.value)} placeholder="05/2026" /></Field>
        </FormRow>
        <Field label="ICP"><Txta rows={2} value={f.icp} onChange={(e) => up("icp", e.target.value)} placeholder="Ortopedista particular, 35–55 anos..." /></Field>
        <Field label="Princípio central"><Txta rows={2} value={f.principio} onChange={(e) => up("principio", e.target.value)} placeholder="O criativo faz o trabalho de segmentação..." /></Field>

        {/* Criativos */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>Criativos</div>
            <Btn sm variant="ghost" onClick={() => addArr(setF, "criativos", { id: "", fase: "TOF", prio: "ALTA", formato: "", campanha: "", hook: "", objetivo: "", cta: "" })}>+ Adicionar</Btn>
          </div>
          {f.criativos.map((cr, i) => (
            <div key={i} style={{ padding: "10px 12px", background: "var(--bg)", borderRadius: 8, border: `1px solid ${"var(--line-soft)"}`, marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Inp style={{ width: 80 }} value={cr.id} onChange={(e) => upArr(setF, "criativos", i, "id", e.target.value)} placeholder="ID (ADS2)" />
                <Sel style={{ width: 80 }} value={cr.fase} onChange={(e) => upArr(setF, "criativos", i, "fase", e.target.value)}>
                  {["TOF", "RTG", "BOF"].map((f) => <option key={f}>{f}</option>)}
                </Sel>
                <Sel style={{ width: 100 }} value={cr.prio} onChange={(e) => upArr(setF, "criativos", i, "prio", e.target.value)}>
                  {["MÁXIMA", "ALTA", "MÉDIA", "BAIXA"].map((p) => <option key={p}>{p}</option>)}
                </Sel>
                <Inp style={{ flex: 1, minWidth: 120 }} value={cr.formato} onChange={(e) => upArr(setF, "criativos", i, "formato", e.target.value)} placeholder="Vídeo 60–90s vertical" />
                <button onClick={() => rmArr(setF, "criativos", i)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Inp value={cr.campanha} onChange={(e) => upArr(setF, "criativos", i, "campanha", e.target.value)} placeholder="Campanha / conjunto de anúncios" />
                <Txta rows={2} value={cr.hook} onChange={(e) => upArr(setF, "criativos", i, "hook", e.target.value)} placeholder="Hook — os 3 primeiros segundos..." />
                <FormRow>
                  <Inp style={{ flex: 1 }} value={cr.objetivo} onChange={(e) => upArr(setF, "criativos", i, "objetivo", e.target.value)} placeholder="Objetivo" />
                  <Inp style={{ flex: 1 }} value={cr.cta} onChange={(e) => upArr(setF, "criativos", i, "cta", e.target.value)} placeholder="CTA" />
                </FormRow>
              </div>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>CTAs por Fase</div>
          {f.ctas.map((c, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: (FV[c.fase] || { color: "var(--muted)" }).color, width: 36, flexShrink: 0 }}>{c.fase}</span>
              <Inp style={{ flex: 1 }} value={c.cta} onChange={(e) => upArr(setF, "ctas", i, "cta", e.target.value)} placeholder="Texto do CTA" />
              <Inp style={{ width: 160 }} value={c.tom} onChange={(e) => upArr(setF, "ctas", i, "tom", e.target.value)} placeholder="Tom" />
            </div>
          ))}
        </div>

        <Btn variant="accent" full onClick={save} disabled={saving || !f.titulo.trim()}>
          {saving ? "Salvando..." : "Salvar guia de criativos"}
        </Btn>
      </div>
    </Modal>
  );
}

// ── PlanoModal ────────────────────────────────────────────────────────────────

function PlanoModal({ clientId, onClose, onSave }) {
  const [f, setF] = useState({
    titulo: "", mes: "",
    campanhas: [],
    checklists: [],
    metricas: [],
    calendario: [],
  });
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  function addGrupo() {
    setF((p) => ({ ...p, checklists: [...p.checklists, { grupo: "", items: [] }] }));
  }
  function upGrupoNome(gi, value) {
    setF((p) => { const arr = [...p.checklists]; arr[gi] = { ...arr[gi], grupo: value }; return { ...p, checklists: arr }; });
  }
  function addItemGrupo(gi) {
    setF((p) => {
      const arr = [...p.checklists];
      arr[gi] = { ...arr[gi], items: [...arr[gi].items, { k: uid(), label: "", done: false }] };
      return { ...p, checklists: arr };
    });
  }
  function upItemGrupo(gi, ii, value) {
    setF((p) => {
      const arr = [...p.checklists];
      const items = [...arr[gi].items];
      items[ii] = { ...items[ii], label: value };
      arr[gi] = { ...arr[gi], items };
      return { ...p, checklists: arr };
    });
  }
  function rmItemGrupo(gi, ii) {
    setF((p) => {
      const arr = [...p.checklists];
      arr[gi] = { ...arr[gi], items: arr[gi].items.filter((_, j) => j !== ii) };
      return { ...p, checklists: arr };
    });
  }
  function addFaseMetrica() {
    setF((p) => ({ ...p, metricas: [...p.metricas, { fase: "TOF", items: [] }] }));
  }
  function upFase(fi, value) {
    setF((p) => { const arr = [...p.metricas]; arr[fi] = { ...arr[fi], fase: value }; return { ...p, metricas: arr }; });
  }
  function addMetricaItem(fi) {
    setF((p) => {
      const arr = [...p.metricas];
      arr[fi] = { ...arr[fi], items: [...arr[fi].items, { metrica: "", meta: "", alerta: "" }] };
      return { ...p, metricas: arr };
    });
  }
  function upMetricaItem(fi, ii, field, value) {
    setF((p) => {
      const arr = [...p.metricas];
      const items = [...arr[fi].items];
      items[ii] = { ...items[ii], [field]: value };
      arr[fi] = { ...arr[fi], items };
      return { ...p, metricas: arr };
    });
  }

  async function save() {
    if (!f.titulo.trim()) return;
    setSaving(true);
    await onSave({
      section: "plano", titulo: f.titulo.trim(), mes: f.mes.trim(),
      dados: {
        campanhas: f.campanhas.filter((c) => c.nome),
        checklists: f.checklists.filter((g) => g.grupo),
        metricas: f.metricas.filter((m) => m.fase),
        calendario: f.calendario.filter((e) => e.acao),
      },
    });
    setSaving(false);
  }

  return (
    <Modal title="Novo plano estratégico" onClose={onClose} width={700}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: "72vh", overflowY: "auto", paddingRight: 4 }}>
        <FormRow>
          <Field label="Título" half><Inp value={f.titulo} onChange={(e) => up("titulo", e.target.value)} placeholder="Plano Estratégico ADS — Maio 2026" /></Field>
          <Field label="Mês" half><Inp value={f.mes} onChange={(e) => up("mes", e.target.value)} placeholder="05/2026" /></Field>
        </FormRow>

        {/* Campanhas */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>Campanhas</div>
            <Btn sm variant="ghost" onClick={() => addArr(setF, "campanhas", { nome: "", acao: "Criar", fase: "TOF", budget: "", objetivo: "", detalhe: "" })}>+ Adicionar</Btn>
          </div>
          {f.campanhas.map((camp, i) => (
            <div key={i} style={{ padding: "10px 12px", background: "var(--bg)", borderRadius: 8, border: `1px solid ${"var(--line-soft)"}`, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap", alignItems: "center" }}>
                <Inp style={{ flex: 1, minWidth: 130 }} value={camp.nome} onChange={(e) => upArr(setF, "campanhas", i, "nome", e.target.value)} placeholder="Nome da campanha" />
                <Sel style={{ width: 110 }} value={camp.acao} onChange={(e) => upArr(setF, "campanhas", i, "acao", e.target.value)}>
                  {["Criar", "Reestruturar", "Pausar", "Monitorar"].map((a) => <option key={a}>{a}</option>)}
                </Sel>
                <Sel style={{ width: 80 }} value={camp.fase} onChange={(e) => upArr(setF, "campanhas", i, "fase", e.target.value)}>
                  {["TOF", "RTG", "BOF"].map((ff) => <option key={ff}>{ff}</option>)}
                </Sel>
                <Inp style={{ width: 90 }} value={camp.budget} onChange={(e) => upArr(setF, "campanhas", i, "budget", e.target.value)} placeholder="R$ 12/dia" />
                <button onClick={() => rmArr(setF, "campanhas", i)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Inp value={camp.objetivo} onChange={(e) => upArr(setF, "campanhas", i, "objetivo", e.target.value)} placeholder="Objetivo" />
                <Inp value={camp.detalhe} onChange={(e) => upArr(setF, "campanhas", i, "detalhe", e.target.value)} placeholder="Detalhes / configuração" />
              </div>
            </div>
          ))}
        </div>

        {/* Checklists */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>Checklists</div>
            <Btn sm variant="ghost" onClick={addGrupo}>+ Grupo</Btn>
          </div>
          {f.checklists.map((grupo, gi) => (
            <div key={gi} style={{ padding: "10px 12px", background: "var(--bg)", borderRadius: 8, border: `1px solid ${"var(--line-soft)"}`, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
                <Inp style={{ flex: 1 }} value={grupo.grupo} onChange={(e) => upGrupoNome(gi, e.target.value)} placeholder="Nome do grupo" />
                <Btn sm variant="ghost" onClick={() => addItemGrupo(gi)}>+ Item</Btn>
                <button onClick={() => rmArr(setF, "checklists", gi)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
              </div>
              {grupo.items.map((item, ii) => (
                <div key={ii} style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center" }}>
                  <span style={{ color: "var(--faint)", fontSize: 13 }}>□</span>
                  <Inp style={{ flex: 1 }} value={item.label} onChange={(e) => upItemGrupo(gi, ii, e.target.value)} placeholder="Descrição do item" />
                  <button onClick={() => rmItemGrupo(gi, ii)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Métricas alvo */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>Métricas Alvo</div>
            <Btn sm variant="ghost" onClick={addFaseMetrica}>+ Fase</Btn>
          </div>
          {f.metricas.map((mf, fi) => (
            <div key={fi} style={{ padding: "10px 12px", background: "var(--bg)", borderRadius: 8, border: `1px solid ${"var(--line-soft)"}`, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
                <Sel style={{ width: 80 }} value={mf.fase} onChange={(e) => upFase(fi, e.target.value)}>
                  {["TOF", "RTG", "BOF"].map((ff) => <option key={ff}>{ff}</option>)}
                </Sel>
                <Btn sm variant="ghost" onClick={() => addMetricaItem(fi)}>+ Métrica</Btn>
                <button onClick={() => rmArr(setF, "metricas", fi)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
              </div>
              {mf.items.map((m, ii) => (
                <div key={ii} style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center" }}>
                  <Inp style={{ flex: 1 }} value={m.metrica} onChange={(e) => upMetricaItem(fi, ii, "metrica", e.target.value)} placeholder="Métrica" />
                  <Inp style={{ width: 90 }} value={m.meta} onChange={(e) => upMetricaItem(fi, ii, "meta", e.target.value)} placeholder="Meta" />
                  <Inp style={{ width: 110 }} value={m.alerta} onChange={(e) => upMetricaItem(fi, ii, "alerta", e.target.value)} placeholder="Alerta" />
                  <button onClick={() => {
                    setF((p) => {
                      const arr = [...p.metricas];
                      arr[fi] = { ...arr[fi], items: arr[fi].items.filter((_, j) => j !== ii) };
                      return { ...p, metricas: arr };
                    });
                  }} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Calendário */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>Calendário</div>
            <Btn sm variant="ghost" onClick={() => addArr(setF, "calendario", { data: "", acao: "" })}>+ Adicionar</Btn>
          </div>
          {f.calendario.map((e, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <Inp style={{ width: 100 }} value={e.data} onChange={(ev) => upArr(setF, "calendario", i, "data", ev.target.value)} placeholder="13–15 mai" />
              <Inp style={{ flex: 1 }} value={e.acao} onChange={(ev) => upArr(setF, "calendario", i, "acao", ev.target.value)} placeholder="Ação" />
              <button onClick={() => rmArr(setF, "calendario", i)} style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>

        <Btn variant="accent" full onClick={save} disabled={saving || !f.titulo.trim()}>
          {saving ? "Salvando..." : "Salvar plano estratégico"}
        </Btn>
      </div>
    </Modal>
  );
}

// ── MetaAdsTab (exported) ─────────────────────────────────────────────────────

const SECTION_LABELS = { diagnostico: "Diagnóstico", criativos: "Criativos", plano: "Plano Estratégico" };
const FORM_TITLE     = { diagnostico: "Nova auditoria", criativos: "Novo guia de criativos", plano: "Novo plano estratégico" };

export function MetaAdsTab({ client, metaAds, onNew, onUpdate, onDelete }) {
  const [section, setSection]   = useState("diagnostico");
  const [showForm, setShowForm] = useState(false);

  const entries = (metaAds || []).filter((m) => m.section === section);

  return (
    <div className="tab-pane">
      <BlockHead title="Meta Ads" sub="Auditorias, guias de criativos e planos. Cada registro abre na linha.">
        <Btn sm variant="primary" onClick={() => setShowForm(true)}>+ {FORM_TITLE[section]}</Btn>
      </BlockHead>

      <Chips label="Seção do Meta Ads" active={section} style={{ marginBottom: 12 }}
        onChange={(s) => setSection(s)}
        items={["diagnostico", "criativos", "plano"].map((s) => ({ id: s, label: SECTION_LABELS[s], count: (metaAds || []).filter((m) => m.section === s).length }))} />

      <ExpandTable
        key={section}
        minWidth={480}
        rows={entries}
        empty={`Nenhum registro de ${SECTION_LABELS[section].toLowerCase()}. Clique em "+ ${FORM_TITLE[section]}" para adicionar.`}
        columns={[
          { key: "titulo", label: "Registro", render: (entry) => <span className="name">{entry.titulo}</span> },
          { key: "mes", label: "Mês", render: (entry) => <span className="sub">{entry.mes || "—"}</span> },
          { key: "del", label: "", align: "right", render: (entry) => (
            <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <Btn sm variant="ghost" onClick={() => { if (window.confirm("Excluir este registro?")) onDelete(entry.id); }}>Excluir</Btn>
            </span>
          ) },
        ]}
        detail={(entry) => (
          <div style={{ paddingTop: 10 }}>
            {section === "diagnostico" && <RenderDiagnostico dados={entry.dados} />}
            {section === "criativos"   && <RenderCriativos dados={entry.dados} />}
            {section === "plano"       && <RenderPlano entry={entry} onUpdate={onUpdate} />}
          </div>
        )}
      />

      {/* Modais por seção */}
      {showForm && section === "diagnostico" && (
        <DiagnosticoModal clientId={client.id} onClose={() => setShowForm(false)}
          onSave={async (data) => { await onNew({ ...data, client_id: client.id }); setShowForm(false); }} />
      )}
      {showForm && section === "criativos" && (
        <CriativosModal clientId={client.id} onClose={() => setShowForm(false)}
          onSave={async (data) => { await onNew({ ...data, client_id: client.id }); setShowForm(false); }} />
      )}
      {showForm && section === "plano" && (
        <PlanoModal clientId={client.id} onClose={() => setShowForm(false)}
          onSave={async (data) => { await onNew({ ...data, client_id: client.id }); setShowForm(false); }} />
      )}
    </div>
  );
}
